import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@monorepo-apps/ui/src/components/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@monorepo-apps/ui/src/components/dialog";
import { Input } from "@monorepo-apps/ui/src/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

import { useCreateDeliveryTask } from "@/application/use-cases/delivery-tasks/use-delivery-tasks.queries";
import {
  BACKLOG_VALUE,
  NO_BLOCK_VALUE,
  UNASSIGNED_VALUE,
} from "@/presentation/components/delivery-tasks/delivery-task-fields";
import { Field } from "@/presentation/components/delivery-tasks/field";
import { formatSprintRange } from "@/presentation/components/delivery-tasks/sprint-dates";
import { useAuth } from "@/presentation/providers/auth.provider";
import type {
  Priority,
  ProfileRow,
  SprintRow,
  TaskStatus,
  WorkstreamRow,
} from "@/infrastructure/supabase/database.types";

/**
 * Create a delivery task. Only the title is required — sprint and block are
 * both optional, so a task can be filed now and sorted later. Mounted only
 * while open so the defaults come from fresh props.
 */
export function CreateDeliveryTaskDialog({
  onClose,
  sprints,
  workstreams,
  assignees,
  defaultSprintId,
}: {
  onClose: () => void;
  sprints: SprintRow[];
  workstreams: WorkstreamRow[];
  assignees: ProfileRow[];
  defaultSprintId: string | null;
}) {
  const { session } = useAuth();
  const create = useCreateDeliveryTask();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<TaskStatus>("todo");
  const [priority, setPriority] = useState<Priority>("medium");
  const [sprintId, setSprintId] = useState<string | null>(defaultSprintId);
  const [workstreamKey, setWorkstreamKey] = useState<string | null>(null);
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState("");
  const [busy, setBusy] = useState(false);

  const userId = session?.user?.id ?? null;
  const canSubmit = !!title.trim() && !!userId && !busy;

  const handleClose = () => {
    setBusy(false);
    onClose();
  };

  const save = async () => {
    if (!canSubmit || !userId) return;
    setBusy(true);
    try {
      await create.mutateAsync({
        title: title.trim(),
        description: description.trim() || null,
        status,
        priority,
        sprint_id: sprintId,
        workstream_key: workstreamKey,
        assignee_id: assigneeId,
        // `due_date` is a `date` column — store the raw yyyy-MM-dd the input
        // produced. Passing it through `new Date(...)` would pin it to UTC
        // midnight and shift the day for anyone west of UTC.
        due_date: dueDate || null,
        created_by: userId,
      });
      toast.success("Task created");
      handleClose();
    } catch (err) {
      setBusy(false);
      toast.error(err instanceof Error ? err.message : "Failed to create task");
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New task</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Field label="Title">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={300}
              placeholder="What needs to happen?"
              autoFocus
              required
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Sprint">
              <Select
                value={sprintId ?? BACKLOG_VALUE}
                onValueChange={(v) => setSprintId(v === BACKLOG_VALUE ? null : v)}
              >
                <SelectTrigger aria-label="Sprint">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={BACKLOG_VALUE}>Backlog (no sprint)</SelectItem>
                  {sprints.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                      <span className="text-muted-foreground ml-1.5 text-[10px]">
                        {formatSprintRange(s.start_date, s.end_date)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Block (optional)">
              <Select
                value={workstreamKey ?? NO_BLOCK_VALUE}
                onValueChange={(v) => setWorkstreamKey(v === NO_BLOCK_VALUE ? null : v)}
              >
                <SelectTrigger aria-label="Block">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_BLOCK_VALUE}>No block</SelectItem>
                  {workstreams.map((w) => (
                    <SelectItem key={w.key} value={w.key}>
                      <span className="font-data text-muted-foreground text-[10px] tracking-wider uppercase">
                        {w.code}
                      </span>{" "}
                      {w.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Description">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              maxLength={5000}
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Status">
              <Select value={status} onValueChange={(v) => setStatus(v as TaskStatus)}>
                <SelectTrigger aria-label="Status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todo">To do</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Priority">
              <Select value={priority} onValueChange={(v) => setPriority(v as Priority)}>
                <SelectTrigger aria-label="Priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Assignee">
              <Select
                value={assigneeId ?? UNASSIGNED_VALUE}
                onValueChange={(v) => setAssigneeId(v === UNASSIGNED_VALUE ? null : v)}
              >
                <SelectTrigger aria-label="Assignee">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED_VALUE}>Unassigned</SelectItem>
                  {assignees.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.email ?? "(no email)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Due date">
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={handleClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {busy ? "Creating…" : "Create task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
